import { isEmpty } from 'lodash';
import React, { useMemo } from 'react';

import { SvgIcon } from '@mui/material';

import IconVirus from 'app/ui-kit/icons/Virus.svg';
import { emptyArray } from 'main/components/SearchBar/constants';
import { isProtectedData } from 'pages/components/ProtectedData';

import MoreTagsChip from './components/MoreTagsChip';
import ProtectedChip from './components/ProtectedChip';
import useStyles from './styles';
import { Data } from './types';

interface Props {
    data: Data;
    compact?: boolean;
    className?: string;
}

const Tags: React.FC<Props> = ({
    data: {
        cwe = emptyArray,
        bounty,
        enchantments: { exploitation, tags }
    },
    compact,
    className
}) => {
    const { classes, cx } = useStyles();
    const size = compact ? 'small' : 'medium';

    const otherTags = useMemo(() => {
        return (tags ?? []).reduce<{ hidden: string[]; visible: string[] }>(
            (output, tag) => {
                if (isProtectedData(tag)) {
                    output.hidden.push(tag);
                } else {
                    output.visible.push(tag);
                }
                return output;
            },
            { hidden: [], visible: [] }
        );
    }, [tags]);

    return (
        <div className={cx(classes.container, className)}>
            <div className={classes.line}>
                {cwe.filter(Boolean).map((cwe, index) => {
                    const cweId = cwe.replace(/\D+/g, '');
                    const url = `https://cwe.mitre.org/data/definitions/${cweId}.html`;
                    return <ProtectedChip key={index} size={size} data={cwe} label={cwe} href={url} />;
                })}
                {bounty ? (
                    <ProtectedChip
                        size={size}
                        data={bounty}
                        tooltip="Bounty Amount"
                        label={({ isProtected, content }) => (isProtected ? content : `$${bounty}`)}
                    />
                ) : null}
                {exploitation?.wildExploited ? (
                    <ProtectedChip
                        size={size}
                        tooltip="Exploited in the wild"
                        data={exploitation.wildExploited}
                        label="In Wild"
                        icon={<SvgIcon className={classes.tagIcon} component={IconVirus} />}
                        href={'/search?query='.concat(
                            exploitation.wildExploitedSources
                                .map(({ idList }) => idList.map((id) => `"${id}"`))
                                .flat()
                                .join(' OR ')
                        )}
                    />
                ) : null}
                {isEmpty(otherTags.hidden) ? null : isEmpty(otherTags.visible) ? (
                    <MoreTagsChip count={otherTags.hidden.length} />
                ) : null}
            </div>
            {isEmpty(otherTags.visible) ? null : (
                <div className={classes.line}>
                    {otherTags.visible.map((tag, index) => (
                        <ProtectedChip
                            size={size}
                            key={index}
                            data={tag}
                            label={tag}
                            href={`/search/tags/${encodeURIComponent(tag)}`}
                        />
                    ))}
                    {isEmpty(otherTags.hidden) ? null : <MoreTagsChip count={otherTags.hidden.length} />}
                </div>
            )}
        </div>
    );
};

export default Tags;
